import React, {FC, useRef, useState} from 'react';
import styled from 'styled-components';

import {
  buildDirectHoverSelector,
  buildHoverParentClassName,
} from '../../../../../../libs/shared/core/src/helpers/browser/hoverHelpers';
import {Trans} from '../../../../../../libs/shared/core/src/helpers/internationalization/react';
import {ConversationInquiryModel} from '../../../../../../libs/shared/core/src/models/api/conversationInquiriesModel';
import {DataEventActionsEnum} from '../../../../../../libs/shared/core/src/models/tracking/dataEventActions';
import {ViewerDataEventObject} from '../../../../../../libs/shared/core/src/models/tracking/objectNames/viewerDataEventObject';
import {ViewerPanelId} from '../../../../../../libs/shared/core/src/store/panels/panelStateBase';
import {ChromelessButton} from '../../../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../../../libs/shared/ui/src/styles/fontStyles';
import {UserThemeConsumer} from '../../../../../../libs/shared/ui/src/theme/userThemeConsumer';
import {useAppDispatch, useAppSelector} from '../../../../../helpers/appReduxHelpers';
import {AppState} from '../../../../../store/appState';
import {makeGetConversationById} from '../../../../../store/data/conversations/conversationsSelectors';
import {popoutConversationViewerAction} from '../../../../../store/panels/viewer/viewerPanelActions';
import {
  getViewerPanelId,
  makeGetViewerPanelPage,
} from '../../../../../store/panels/viewer/viewerPanelSelectors';
import {ConversationPreviewContainer} from '../../../../common/conversations/conversationPreviewContainer';
import {ConversationCellContainer} from '../../../cells/conversationCellContainer';
import {useSynopsisViewerContext} from './synopsisViewerContext';

interface SynopsisConversationPreviewProps {
  conversationId: number;
  similarInquiry?: ConversationInquiryModel;
  panelId: ViewerPanelId;
  trackEventObjectData: Record<string, unknown>;
}

/*
 * Styles.
 */

const StyledConversationViewButton = styled(ChromelessButton)`
  min-height: 84px;
  width: 260px;
  position: relative;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid ${(p) => p.theme.alphas.black20};
`;

const StyledOverlayDiv = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;

  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  padding: 7px 8px 8px 8px;

  border-radius: 8px;
  background-color: ${(p) => p.theme.alphas.white60};

  transition: opacity 300ms ease;
  opacity: 0;

  ${buildDirectHoverSelector()} {
    opacity: 1;
  }
`;

const StyledDetailsDiv = styled.div`
  display: grid;
  grid-template-areas: 'text icon';
  grid-template-columns: 1fr auto;
  grid-column-gap: 4px;
  align-items: center;
`;

const StyledTextDiv = styled.div`
  grid-area: text;
  padding-left: 4px;
  font-weight: ${fontWeights.semibold};
`;

const StyledIconDiv = styled.div`
  grid-area: icon;
  padding: 2px;
  margin-right: 2px;
  border-radius: 50%;
  background: ${(p) => p.theme.palette.blue.shade40};
`;

/*
 * Selectors.
 */

function makeGetCellsContext(panelId: ViewerPanelId) {
  return (state: AppState) => {
    const viewerPage = makeGetViewerPanelPage(getViewerPanelId)(state, {panelId});
    return viewerPage.cellsContext;
  };
}

export const SynopsisConversationPreview: FC<SynopsisConversationPreviewProps> = ({
  conversationId,
  similarInquiry,
  trackEventObjectData,
  panelId,
}) => {
  const {isInPopup, onTrackDataEvent} = useSynopsisViewerContext();

  const dispatch = useAppDispatch();
  const buttonRef = useRef(null);
  const [shouldShowPreview, setShouldShowPreview] = useState(false);

  const conversation = useAppSelector(makeGetConversationById(conversationId));
  const cellsContext = useAppSelector(makeGetCellsContext(panelId));

  if (!conversation) {
    return null;
  }

  // Standalone window is too narrow to fit horizontally.
  const placement = isInPopup ? 'bottom' : 'right';

  return (
    <StyledConversationViewButton
      className={buildHoverParentClassName()}
      onMouseEnter={() => setShouldShowPreview(true)}
      onMouseLeave={() => setShouldShowPreview(false)}
      onClick={() => {
        onTrackDataEvent(DataEventActionsEnum.CLICK, ViewerDataEventObject.SYNOPSIS_ITEM_RELATED, {
          related_id: conversationId,
          ...trackEventObjectData,
        });

        dispatch(popoutConversationViewerAction(conversationId, similarInquiry?.activityId));
      }}
      ref={buttonRef}
    >
      <ConversationCellContainer
        panelId={panelId}
        conversation={conversation}
        cellsContext={cellsContext}
        isExtraConversation={false}
        parameters={undefined}
        isPreview
      />
      {renderOverlay()}
      {shouldShowPreview && (
        <ConversationPreviewContainer
          panelId={panelId}
          conversation={conversation}
          similarInquiry={similarInquiry}
          anchor={buttonRef}
          viewerConversationId={undefined}
          shouldRenderSummaryIfAvailable
          placement={placement}
        />
      )}
    </StyledConversationViewButton>
  );
};

/*
 * Helpers.
 */

function renderOverlay() {
  return (
    <UserThemeConsumer>
      {(theme) => (
        <StyledOverlayDiv>
          <StyledDetailsDiv>
            <StyledTextDiv>
              <Trans id="synopsis-similar-view">View in new window</Trans>
            </StyledTextDiv>
            <StyledIconDiv>
              <Icon name="externalLinkTiny" color={theme.greys.white} />
            </StyledIconDiv>
          </StyledDetailsDiv>
        </StyledOverlayDiv>
      )}
    </UserThemeConsumer>
  );
}
